/**
 * MonthRow Component
 *
 * Displays a single month's configuration as an expandable row.
 * Clicking "Zobacz szczegóły" expands to show:
 * - All invoice parameters (compact grid layout)
 * - Hourly consumption pattern chart
 */

import { useState } from 'react';
import {
  Paper,
  Group,
  Text,
  Button,
  Collapse,
  Stack,
  Grid,
  Select,
  TextInput,
  Box,
} from '@mantine/core';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type {
  MonthlyConfiguration,
  TariffType,
  ProHourlyPatternType,
  MonthIndex,
} from '../../../../types/proCalculator';
import {
  PRO_MONTH_NAMES,
  INVOICE_PARAMETER_METADATA,
} from '../../../../types/proCalculator';

interface MonthRowProps {
  /** Month index (0-11) */
  monthIndex: number;

  /** Month configuration data */
  month: MonthlyConfiguration;

  /** Callback when tariff changes */
  onTariffChange: (tariff: TariffType) => void;

  /** Callback when consumption changes */
  onConsumptionChange: (consumption: number) => void;

  /** Callback when hourly pattern changes */
  onHourlyPatternChange: (pattern: ProHourlyPatternType) => void;

  /** Callback when parameter value changes */
  onParameterChange: (parameterKey: string, value: number) => void;

  /** Available hourly pattern options */
  hourlyPatternOptions: Array<{ value: string; label: string }>;

  /** Hourly pattern data for chart */
  hourlyPatternData?: { weekday: number[]; weekend: number[] };
}

/**
 * MonthRow Component
 *
 * Single expandable row for one month's configuration
 */
export function MonthRow({
  monthIndex,
  month,
  onTariffChange,
  onConsumptionChange,
  onHourlyPatternChange,
  onParameterChange,
  hourlyPatternOptions,
  hourlyPatternData,
}: MonthRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  // Tariff options
  const tariffOptions: Array<{ value: TariffType; label: string }> = [
    { value: 'G11', label: 'G11' },
    { value: 'G12', label: 'G12' },
    { value: 'G12w', label: 'G12w' },
    { value: 'G12as', label: 'G12as' },
    { value: 'G13', label: 'G13' },
    { value: 'G13s', label: 'G13s' },
  ];

  // Prepare chart data for hourly consumption
  const chartData =
    hourlyPatternData
      ? Array.from({ length: 24 }, (_, hour) => ({
          hour: `${hour}:00`,
          'Dzień roboczy': (hourlyPatternData.weekday[hour] * 100).toFixed(2),
          'Weekend': (hourlyPatternData.weekend[hour] * 100).toFixed(2),
        }))
      : [];

  // Invoice parameters (all 11)
  const invoiceParameters = [
    'energyElectricCost',
    'handlingFee',
    'networkFixedFee',
    'transitionFee',
    'qualityFee',
    'networkVariableFee',
    'ozeFee',
    'cogenerationFee',
    'subscriptionFee',
    'powerFee',
  ] as const;

  return (
    <Paper shadow="xs" p="md" withBorder>
      <Stack gap="md">
        {/* Row Header - Always Visible */}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="lg" wrap="nowrap" style={{ flex: 1 }}>
            {/* Month Name */}
            <Text fw={600} size="lg" style={{ minWidth: 100 }}>
              {PRO_MONTH_NAMES[monthIndex as MonthIndex]}
            </Text>

            {/* Tariff */}
            <Box style={{ width: 120 }}>
              {monthIndex === 0 && <input type="hidden" data-testid="first-month-tariff" value={month.tariff} />}
              <Select
                data={tariffOptions}
                value={month.tariff}
                onChange={(value) => onTariffChange(value as TariffType)}
                size="sm"
                label="Taryfa"
              />
            </Box>

            {/* Consumption */}
            <Box style={{ width: 150 }}>
              {monthIndex === 0 && <input type="hidden" data-testid="first-month-consumption" value={month.consumption} />}
              <TextInput
                label="Zużycie (kWh)"
                value={month.consumption}
                onChange={(e) => onConsumptionChange(parseFloat(e.target.value) || 0)}
                size="sm"
                type="number"
                min={0}
              />
            </Box>

            {/* Hourly Pattern */}
            <Box style={{ width: 200 }}>
              {monthIndex === 0 && <input type="hidden" data-testid="first-month-pattern" value={month.hourlyPattern} />}
              <Select
                data={hourlyPatternOptions}
                value={month.hourlyPattern}
                onChange={(value) => onHourlyPatternChange(value as ProHourlyPatternType)}
                size="sm"
                label="Wzorzec godzinowy"
              />
            </Box>
          </Group>

          {/* Expand/Collapse Button */}
          <Button
            variant="subtle"
            size="sm"
            onClick={toggleExpanded}
            rightSection={isExpanded ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
          >
            {isExpanded ? 'Ukryj szczegóły' : 'Zobacz szczegóły'}
          </Button>
        </Group>

        {/* Expandable Details Section */}
        <Collapse in={isExpanded}>
          <Stack gap="lg" pt="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
            {/* Invoice Parameters - Compact Grid */}
            <Box>
              <Text fw={600} size="sm" mb="xs">
                Parametry faktury
              </Text>
              <Grid gutter="xs">
                {invoiceParameters.map((paramKey) => {
                  const metadata = INVOICE_PARAMETER_METADATA[paramKey];
                  const paramValue = month.parameters[paramKey].valueFinal;

                  return (
                    <Grid.Col key={paramKey} span={4}>
                      <TextInput
                        label={metadata.name}
                        value={paramValue.toFixed(2)}
                        onChange={(e) =>
                          onParameterChange(paramKey, parseFloat(e.target.value) || 0)
                        }
                        size="xs"
                        type="number"
                        min={0}
                        step={0.01}
                        rightSection={<Text size="xs" c="dimmed">zł</Text>}
                      />
                    </Grid.Col>
                  );
                })}
              </Grid>
            </Box>

            {/* Hourly Consumption Chart */}
            {hourlyPatternData && chartData.length > 0 && (
              <Box>
                <Text fw={600} size="sm" mb="xs">
                  Wzorzec zużycia godzinowego (%)
                </Text>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Dzień roboczy" fill="var(--mantine-color-blue-6)" />
                    <Bar dataKey="Weekend" fill="var(--mantine-color-green-6)" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
}
